import { useState, useCallback } from "react";
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragStartEvent,
  type DragEndEvent,
} from "@dnd-kit/core";
import { arrayMove, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { CodePalette, createBlock } from "./CodePalette";
import { CodeSequence } from "./CodeSequence";
import { CodeBlock } from "./CodeBlock";
import type { ProgramEditorProps, CodeBlockData, BlockType } from "./types";

/**
 * Default available blocks
 */
const DEFAULT_BLOCKS: BlockType[] = [
  "move_up",
  "move_down",
  "move_left",
  "move_right",
];

/**
 * ProgramEditor Component
 *
 * The complete visual programming interface that combines the palette,
 * sequence editor, and play controls. Manages drag-and-drop state and
 * program execution.
 *
 * @example
 * <ProgramEditor
 *   blocks={programBlocks}
 *   onBlocksChange={setProgramBlocks}
 *   onPlay={executeProgram}
 *   onReset={resetMaze}
 *   isExecuting={isRunning}
 * />
 */
export function ProgramEditor({
  blocks,
  onBlocksChange,
  onPlay,
  onReset,
  isExecuting = false,
  availableBlocks = DEFAULT_BLOCKS,
  maxBlocks = 10,
  hasWon = false,
  executingBlockId = null,
}: ProgramEditorProps) {
  const [activeBlock, setActiveBlock] = useState<CodeBlockData | null>(null);
  const [dragSource, setDragSource] = useState<"palette" | "sequence" | null>(null);

  // Configure sensors for both mouse and touch
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px movement before drag starts
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 150, // 150ms hold before drag starts
        tolerance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle drag start
  const handleDragStart = useCallback((event: DragStartEvent) => {
    const { active } = event;
    const activeId = String(active.id);

    // Check if dragging from palette
    if (activeId.startsWith("palette-")) {
      const blockType = active.data.current?.blockType as BlockType;
      setDragSource("palette");
      setActiveBlock(createBlock(blockType));
    } else {
      // Dragging from sequence
      const block = blocks.find((b) => b.id === activeId);
      if (block) {
        setDragSource("sequence");
        setActiveBlock(block);
      }
    }
  }, [blocks]);

  // Handle drag end
  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;

    if (!over) {
      // Dropped outside - reset
      setActiveBlock(null);
      setDragSource(null);
      return;
    }

    const activeId = String(active.id);
    const overId = String(over.id);

    // From palette to sequence
    if (dragSource === "palette" && activeBlock && blocks.length < maxBlocks) {
      if (overId === "sequence" || blocks.some((b) => b.id === overId)) {
        // Find insert position
        let insertIndex = blocks.length;
        if (overId !== "sequence") {
          const overIndex = blocks.findIndex((b) => b.id === overId);
          if (overIndex !== -1) {
            insertIndex = overIndex;
          }
        }

        const newBlocks = [...blocks];
        newBlocks.splice(insertIndex, 0, activeBlock);
        onBlocksChange(newBlocks);
      }
    }

    // Reorder within sequence
    if (dragSource === "sequence") {
      const oldIndex = blocks.findIndex((b) => b.id === activeId);
      const newIndex = blocks.findIndex((b) => b.id === overId);

      if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
        onBlocksChange(arrayMove(blocks, oldIndex, newIndex));
      }
    }

    setActiveBlock(null);
    setDragSource(null);
  }, [activeBlock, blocks, dragSource, maxBlocks, onBlocksChange]);

  // Handle block deletion
  const handleDelete = useCallback((blockId: string) => {
    onBlocksChange(blocks.filter((b) => b.id !== blockId));
  }, [blocks, onBlocksChange]);

  // Handle reorder from CodeSequence
  const handleReorder = useCallback((newBlocks: CodeBlockData[]) => {
    onBlocksChange(newBlocks);
  }, [onBlocksChange]);

  const canPlay = blocks.length > 0 && !isExecuting;

  return (
    <div className="w-full">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        {/* Main editor layout */}
        <div className="grid md:grid-cols-[280px_1fr] gap-6">
          {/* Palette (left side on desktop, top on mobile) */}
          <div className="order-2 md:order-1">
            <CodePalette
              availableBlocks={availableBlocks}
              disabled={isExecuting}
            />
          </div>

          {/* Sequence and controls (right side on desktop, bottom on mobile) */}
          <div className="order-1 md:order-2 flex flex-col gap-4">
            {/* Program sequence */}
            <CodeSequence
              blocks={blocks}
              onReorder={handleReorder}
              onDelete={handleDelete}
              disabled={isExecuting}
              maxBlocks={maxBlocks}
              executingBlockId={executingBlockId}
            />

            {/* Control buttons */}
            <div className="flex gap-4">
              {/* Play button */}
              <button
                onClick={onPlay}
                disabled={!canPlay}
                className={`
                  flex-1
                  flex items-center justify-center gap-3
                  min-h-[72px] px-8
                  font-display font-bold text-xl
                  rounded-2xl
                  border-4
                  transition-all duration-200
                  focus:outline-none focus-visible:ring-4 focus-visible:ring-meadow-300 focus-visible:ring-offset-2
                  touch-manipulation
                  ${canPlay
                    ? `
                      bg-gradient-to-b from-meadow-400 via-meadow-500 to-meadow-600
                      text-white
                      border-meadow-600
                      shadow-[0_4px_0_0_theme(colors.meadow.700),0_6px_12px_rgba(0,0,0,0.15)]
                      hover:shadow-[0_6px_0_0_theme(colors.meadow.700),0_8px_16px_rgba(0,0,0,0.2)]
                      hover:-translate-y-0.5
                      active:shadow-[0_2px_0_0_theme(colors.meadow.700),0_3px_6px_rgba(0,0,0,0.1)]
                      active:translate-y-0.5
                    `
                    : `
                      bg-neutral-200
                      text-neutral-400
                      border-neutral-300
                      cursor-not-allowed
                    `
                  }
                  ${isExecuting ? "animate-pulse" : ""}
                `}
                aria-label={isExecuting ? "Running program..." : "Run program"}
              >
                {/* Play icon */}
                <div
                  className={`
                    flex items-center justify-center
                    w-10 h-10
                    rounded-xl
                    ${canPlay ? "bg-white/20" : "bg-neutral-300"}
                    transition-colors duration-200
                  `}
                >
                  {isExecuting ? (
                    <div className="flex gap-1">
                      <div className="w-1.5 h-6 bg-current rounded-full animate-pulse" />
                      <div className="w-1.5 h-6 bg-current rounded-full animate-pulse" style={{ animationDelay: "0.15s" }} />
                    </div>
                  ) : (
                    <span className="text-2xl">▶️</span>
                  )}
                </div>
                <span>{isExecuting ? "Running..." : "Run!"}</span>
              </button>

              {/* Reset button */}
              <button
                onClick={onReset}
                disabled={isExecuting}
                className={`
                  flex items-center justify-center gap-2
                  min-h-[72px] px-6
                  font-display font-bold text-lg
                  rounded-2xl
                  border-4
                  transition-all duration-200
                  focus:outline-none focus-visible:ring-4 focus-visible:ring-fairy-300 focus-visible:ring-offset-2
                  touch-manipulation
                  ${!isExecuting
                    ? `
                      bg-white
                      text-neutral-600
                      border-neutral-300
                      shadow-[0_4px_0_0_theme(colors.neutral.400),0_6px_12px_rgba(0,0,0,0.1)]
                      hover:border-fairy-300
                      hover:text-fairy-600
                      hover:shadow-[0_6px_0_0_theme(colors.neutral.400),0_8px_16px_rgba(0,0,0,0.15)]
                      hover:-translate-y-0.5
                      active:shadow-[0_2px_0_0_theme(colors.neutral.400),0_3px_6px_rgba(0,0,0,0.08)]
                      active:translate-y-0.5
                    `
                    : `
                      bg-neutral-100
                      text-neutral-400
                      border-neutral-200
                      cursor-not-allowed
                    `
                  }
                `}
                aria-label="Reset program and maze"
              >
                <span className="text-xl">🔄</span>
                <span>Reset</span>
              </button>
            </div>

            {/* Victory celebration message */}
            {hasWon && (
              <div
                className="
                  flex items-center justify-center gap-4
                  py-4 px-6
                  bg-gradient-to-r from-sunshine-100 via-sunshine-50 to-sunshine-100
                  rounded-2xl
                  border-2 border-sunshine-300
                  animate-celebrate
                "
              >
                <span className="text-3xl">🎉</span>
                <div className="text-center">
                  <p className="font-display font-bold text-xl text-sunshine-700">
                    Amazing!
                  </p>
                  <p className="font-body text-sm text-sunshine-600">
                    Your program worked perfectly!
                  </p>
                </div>
                <span className="text-3xl">🎉</span>
              </div>
            )}
          </div>
        </div>

        {/* Drag overlay - shows the block being dragged */}
        <DragOverlay
          dropAnimation={{
            duration: 200,
            easing: "cubic-bezier(0.34, 1.56, 0.64, 1)",
          }}
        >
          {activeBlock && (
            <div className="transform rotate-3 scale-105">
              <CodeBlock
                block={activeBlock}
                isDragging
              />
            </div>
          )}
        </DragOverlay>
      </DndContext>
    </div>
  );
}

export default ProgramEditor;
